import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';

const CATEGORIES = [
  { key: 'agents',    icon: '\uD83D\uDC64', label: 'AGENTS',    path: '/agents',           renderName: r => `${r.prenom} ${r.nom}`, renderSub: r => `${r.matricule || ''} — ${r.poste || 'Agent'}` },
  { key: 'sites',     icon: '\uD83D\uDCCD', label: 'SITES',     path: '/sites',            renderName: r => r.nom,                  renderSub: r => `${r.client_nom || r.ville || r.adresse || ''}` },
  { key: 'clients',   icon: '\uD83C\uDFE2', label: 'CLIENTS',   path: '/clients',          renderName: r => r.raison_sociale,        renderSub: r => r.nom_contact || r.telephone || '' },
  { key: 'incidents', icon: '\u26A0\uFE0F',  label: 'INCIDENTS', path: '/incidents',        renderName: r => r.titre,                renderSub: r => `${r.gravite || ''} — ${r.statut || ''}` },
  { key: 'factures',  icon: '\uD83D\uDCCB', label: 'FACTURES',  path: '/facturation',      renderName: r => r.numero,               renderSub: r => `${r.client_nom || ''} — ${r.montant_ttc ? Number(r.montant_ttc).toLocaleString('fr-FR') + ' FCFA' : ''}` },
  { key: 'contrats',  icon: '\uD83D\uDCC4', label: 'CONTRATS',  path: '/contrats-travail', renderName: r => r.reference,             renderSub: r => `${r.agent_prenom || ''} ${r.agent_nom || ''} — ${r.type || ''}` },
];

export default function GlobalSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Build flat list of all results for keyboard nav
  const flatResults = [];
  CATEGORIES.forEach(cat => {
    (results[cat.key] || []).forEach(r => flatResults.push({ ...r, _cat: cat }));
  });

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults({});
      setActiveIdx(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults({}); return; }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
      setActiveIdx(-1);
    } catch {
      setResults({});
    }
    setLoading(false);
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 250);
  };

  const goTo = (path) => { onClose(); navigate(path); };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flatResults.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && flatResults[activeIdx]) {
        goTo(flatResults[activeIdx]._cat.path);
      } else if (flatResults.length > 0) {
        goTo(flatResults[0]._cat.path);
      }
    }
  };

  const totalResults = flatResults.length;

  if (!isOpen) return null;

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div className="search-input-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="search-icon-svg">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            type="text"
            placeholder="Rechercher agents, sites, clients, incidents..."
            value={query}
            onChange={handleInput}
            className="search-input"
          />
          <kbd className="search-kbd" onClick={onClose}>ESC</kbd>
        </div>

        {loading && <div className="search-loading">Recherche en cours...</div>}

        {!loading && query.length >= 2 && (
          <div className="search-results">
            {totalResults === 0 && <div className="search-empty">Aucun resultat pour "{query}"</div>}

            {CATEGORIES.map(cat => {
              const items = results[cat.key] || [];
              if (items.length === 0) return null;
              return (
                <div className="search-group" key={cat.key}>
                  <div className="search-group-title">{cat.icon} {cat.label} ({items.length})</div>
                  {items.map((r, i) => {
                    const flatIdx = flatResults.findIndex(f => f.id === r.id && f._cat.key === cat.key);
                    return (
                      <button
                        key={r.id}
                        className={`search-result${flatIdx === activeIdx ? ' search-result-active' : ''}`}
                        onClick={() => goTo(cat.path)}
                        onMouseEnter={() => setActiveIdx(flatIdx)}
                      >
                        <span className="search-result-icon">{cat.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="search-result-name">{cat.renderName(r)}</div>
                          <div className="search-result-sub">{cat.renderSub(r)}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {!loading && query.length < 2 && (
          <div className="search-hints">
            <div style={{ padding: '16px 20px', fontSize: 12, color: '#999', textAlign: 'center' }}>
              Tapez au moins 2 caracteres pour rechercher
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
